import { useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import { SAML_USER_ROLE_MANAGEMENT } from '../../../../../shared/featureFlags/flagNames';
import { UserTeamAndRoleManagement } from '../../../../../shared/organizations/types';
import { Action, ResourceType } from '../../../../../typings/permissions';
import { useParams } from '../../../../contexts/RouterContext';
import { useIsFeatureEnabled } from '../../../../helpers/features';
import { useHasPermissionForTeamMembers, useHasPermissionForTeamProjects } from '../../../../helpers/permissions';
import { totalProjectCount } from '../../../../helpers/teams';
import { useTeam } from '../../../../hooks/api/teams';
import { useHasPermission } from '../../../../shared/permissions';
import Loader from '../../../generic/Loader';
import Tabs from '../../../generic/Tabs';
import Header from '../../../shared/settings/SettingsPageHeader';
import SettingsPageTableActions from '../../../shared/settings/SettingsPageTableActions';
import useUserRoleManagement from '../hooks/useUserRoleManagement';
import type { UpdateTeamRequest } from '../../../../../shared/types/routes/teams';

import { EditTeamModal } from './CreateEditTeamModal';
import TeamMembersTable from './management/TeamMembersTable';
import TeamProjects from './TeamProjects';

/**
 * This is the "management" page for a team, which allows you to manage the team's members and projects.
 */
const TeamSettings: VoidFunctionComponent = () => {
  const params = useParams<{ id: string }>();
  const { data: team, isLoading: isTeamLoading } = useTeam(Number(params.id));

  //  Permissions
  const hasPermission = useHasPermission();
  const hasPermissionForTeamMembers = useHasPermissionForTeamMembers();
  const hasPermissionForTeamProjects = useHasPermissionForTeamProjects();
  const isUserRoleManagementEnabled = useIsFeatureEnabled(SAML_USER_ROLE_MANAGEMENT);
  const { data: userRoleManagement } = useUserRoleManagement(isUserRoleManagementEnabled);
  const isTeamRoleIDPManaged = isUserRoleManagementEnabled &&
    userRoleManagement?.teams === UserTeamAndRoleManagement.IDP;
  const isTeamMembershipIDPManaged = isUserRoleManagementEnabled &&
    (userRoleManagement?.teams === UserTeamAndRoleManagement.IDP ||
      userRoleManagement?.teams === UserTeamAndRoleManagement.IDP_TEAMS_FOSSA_ROLES);

  //  State
  const [searchValue, setSearchValue] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState<'members' | 'projects'>('members');
  const [modalToShow, setModalToShow] = useState<'members' | 'projects' | null>(null);

  //  Mostly for type safety
  if (!team) {
    return null;
  }

  if (isTeamLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '300px' }}>
        <Loader />
      </div>
    );
  }

  const canAdd =
    (activeTab === 'members' &&
      hasPermissionForTeamMembers(Action.Edit, team.id) &&
      !isTeamMembershipIDPManaged) ||
    (activeTab === 'projects' && hasPermissionForTeamProjects(Action.Add, team.id));

  const canEditTeam = hasPermission({ resourceType: ResourceType.TeamAny, action: Action.Edit })
    || hasPermission({ resourceType: ResourceType.Team, action: Action.Edit, teamId: team.id });

  return (
    <div className="team-settings">
      <Header heading={team.name}>
        <Header.BackLink to="/account/settings/organization/teams" text="Back to Teams" />
        <Header.TooltipButton
          id="edit-team-group"
          isDisabled={!canEditTeam}
          onClick={() => setIsEditing(true)}
          showTooltip={!canEditTeam}
          tooltip="You do not have permission to edit this team group."
        >
          Edit Team
        </Header.TooltipButton>
      </Header>
      <Tabs>
        <Tabs.Tab
          label="Members"
          isActive={activeTab === 'members'}
          onSelect={() => setActiveTab('members')}
          badgeCount={team.teamUsers?.length || 0}
        />
        <Tabs.Tab
          label="Projects"
          isActive={activeTab === 'projects'}
          onSelect={() => setActiveTab('projects')}
          badgeCount={totalProjectCount(team)}
        />
      </Tabs>
      <SettingsPageTableActions>
        <SettingsPageTableActions.TooltipButton
          id="add-team-members-button"
          type="secondary"
          size="large"
          showTooltip={!canAdd}
          tooltip={`You are not permitted to add ${activeTab} to this team.`}
          onClick={() => setModalToShow(activeTab)}
          isDisabled={!canAdd}
        >
          Add {activeTab === 'members' ? 'Members' : 'Projects'}
        </SettingsPageTableActions.TooltipButton>
        <SettingsPageTableActions.SearchInput
          label={activeTab === 'members' ? 'Search team members' : 'Search team projects'}
          placeholder={activeTab === 'members' ? 'Search team members' : 'Search team projects'}
          value={searchValue}
          onChange={setSearchValue}
          size="medium"
        />
      </SettingsPageTableActions>
      <EditTeamModal
        isOpen={isEditing}
        onHide={() => setIsEditing(false)}
        team={team as UpdateTeamRequest}
      />
      {activeTab === 'members' ? (
        <TeamMembersTable
          team={team}
          search={searchValue}
          isShowingAddMembersModal={modalToShow === 'members'}
          isTeamRoleIDPManaged={isTeamRoleIDPManaged}
          isTeamMembershipIDPManaged={isTeamMembershipIDPManaged}
          onHideAddMembersModal={() => setModalToShow(null)}
        />
      ) : (
        <TeamProjects
          team={team}
          search={searchValue}
          isShowingAddProjectsModal={modalToShow === 'projects'}
          onHideAddProjectsModal={() => setModalToShow(null)}
        />
      )}
    </div>
  );
};

export default TeamSettings;
